import angular from 'angular';
import '../../layouts.module';
import template from './automation-guide.component.html';
import './automation-guide.component.scss';
import { MENU_ITEMS } from '../../../core/constants/menu.constant';
import {
  AUTOMATION_GUIDE_ID,
  AUTOMATION_GUIDE_STEPS,
  AGENT_INSTRUCTION_EXAMPLE,
  visibleGuideSteps
} from './automation-guide.content';

export const GUIDE_OPEN_EVENT = 'guide:open';

const AUTOMATION_ITEMS = MENU_ITEMS.find((section) => section.guide === AUTOMATION_GUIDE_ID).items;

export function isAutomationState(stateName) {
  return AUTOMATION_ITEMS.some(
    (item) => stateName === item.state || (stateName || '').startsWith(`${item.state}.`)
  );
}

class AutomationGuideController {
  constructor(
    EventBusService,
    StorageService,
    STORAGE_KEYS,
    SessionService,
    NotificationService,
    $transitions,
    $state,
    $element,
    $timeout,
    $window
  ) {
    this.EventBusService = EventBusService;
    this.StorageService = StorageService;
    this.STORAGE_KEYS = STORAGE_KEYS;
    this.SessionService = SessionService;
    this.NotificationService = NotificationService;
    this.$transitions = $transitions;
    this.$state = $state;
    this.$element = $element;
    this.$timeout = $timeout;
    this.$window = $window;
    this.steps = AUTOMATION_GUIDE_STEPS;
    this.instructionExample = AGENT_INSTRUCTION_EXAMPLE;
    this.isOpen = false;
    this.index = 0;
  }

  $onInit() {
    this.subscription = this.EventBusService.on(GUIDE_OPEN_EVENT).subscribe((guideId) => {
      if (guideId === AUTOMATION_GUIDE_ID) this.open(0);
    });
    this.removeTransitionHook = this.$transitions.onSuccess({}, (transition) =>
      this.openFirstTime(transition.to().name)
    );
    this.openFirstTime(this.$state.current.name);
  }

  $onDestroy() {
    this.subscription.unsubscribe();
    this.removeTransitionHook();
  }

  get storageKey() {
    const user = this.SessionService.user || {};
    return `${AUTOMATION_GUIDE_ID}:${this.SessionService.tenantSlug || ''}:${user.id || ''}`;
  }

  get step() {
    return this.steps[this.index];
  }

  get isFirst() {
    return this.index === 0;
  }

  get isLast() {
    return this.index === this.steps.length - 1;
  }

  get canCreateAgent() {
    return this.SessionService.hasPermission('ai_agent.create');
  }

  seen() {
    const guides = this.StorageService.get(this.STORAGE_KEYS.GUIDES) || {};
    return Boolean(guides[this.storageKey]);
  }

  remember(outcome) {
    const guides = this.StorageService.get(this.STORAGE_KEYS.GUIDES) || {};
    this.StorageService.set(this.STORAGE_KEYS.GUIDES, { ...guides, [this.storageKey]: outcome });
  }

  openFirstTime(stateName) {
    if (this.isOpen || !isAutomationState(stateName) || this.seen()) return;
    this.open(0);
  }

  open(index) {
    this.steps = visibleGuideSteps((permission) => this.SessionService.hasPermission(permission));
    this.isOpen = true;
    this.goTo(index);
  }

  goTo(index) {
    this.index = Math.max(0, Math.min(index, this.steps.length - 1));
    this.$timeout(() => {
      const title = this.$element[0].querySelector('.cp-automation-guide__title');
      if (title) title.focus();
    });
  }

  next() {
    this.goTo(this.index + 1);
  }

  back() {
    this.goTo(this.index - 1);
  }

  close(outcome = 'dismissed') {
    this.remember(outcome);
    this.isOpen = false;
  }

  finish() {
    this.close('completed');
  }

  canOpen(link) {
    return (
      Boolean(this.$state.get(link.state)) && this.SessionService.hasPermission(link.permission)
    );
  }

  openModule(link) {
    this.close('completed');
    return this.$state.go(link.state, link.params || {});
  }

  startAgent() {
    this.close('completed');
    return this.$state.go(
      this.steps[this.steps.length - 1].action.state,
      this.steps[this.steps.length - 1].action.params
    );
  }

  copyExample() {
    const clipboard = this.$window.navigator.clipboard;
    if (!clipboard) {
      this.NotificationService.error(
        'Copie o texto manualmente: seu navegador bloqueou a área de transferência.'
      );
      return null;
    }
    return clipboard.writeText(this.instructionExample).then(
      () =>
        this.NotificationService.success(
          'Exemplo copiado. Cole e adapte no campo de instruções do agente.'
        ),
      () => this.NotificationService.error('Não foi possível copiar o exemplo.')
    );
  }
}

AutomationGuideController.$inject = [
  'EventBusService',
  'StorageService',
  'STORAGE_KEYS',
  'SessionService',
  'NotificationService',
  '$transitions',
  '$state',
  '$element',
  '$timeout',
  '$window'
];

angular.module('app.layouts').component('cpAutomationGuide', {
  controller: AutomationGuideController,
  controllerAs: 'vm',
  template
});
